/**
 * Pear — React entry point.
 *
 * Wave R2: React takes over rendering entirely. The vanilla shell (main.ts)
 * and the hand-authored markup in index.html are retired; App renders the
 * header, pair screen, tab bar and thread from state instead of toggling
 * `hidden` attributes by hand.
 *
 * YQnX18mKWGxG: the tree is wrapped in one top-level ErrorBoundary so a
 * component crash renders a Velvet Room reload screen instead of a white
 * screen, and the offline-shell service worker registers on the deployed
 * origin only.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { ErrorBoundary } from './ui/ErrorBoundary';
import { installClientErrorHandlers } from './lib/clientErrors';
import { Legal } from './screens/Legal';
import { legalDocForPath } from './lib/legal';
import { captureReferralFromUrl } from './lib/referrals';
import { captureSharePath } from './lib/shareTarget';
import { startBuildWatch } from './lib/buildWatch';

/* Error visibility (card bdsxKQG9HrOQ): window.onerror + unhandledrejection
   are installed BEFORE React mounts, so a crash in the very first render is
   already reported. One reporter, deduped per session, never throws. */
installClientErrorHandlers();

/* Referral capture (card 3MWZ8Zs7jA7e) — the ONE side effect that has to run
   before anything renders: a friend arriving on ?ref=<code> must be remembered
   this launch, whatever screen they land on next. It only reads the URL. */
captureReferralFromUrl();

/* Share target (card jZn7vpkB7EG1) — Android's share sheet lands on /share.
   Read the payload, park it for the app, and put the address bar straight
   back to "/" so /privacy, /terms, the hash deep links and every walk that
   waits on a known path keep resolving exactly as before. It only reads the
   URL: nothing is fetched, nothing is posted, no save happens here. */
captureSharePath(window.location.pathname);

const container = document.getElementById('root');
if (container) {
  /* /privacy and /terms (card cEchedAassmt) — a standalone screen swapped in
     for the app by pathname, so the policy pages load no session and make no
     API calls. The app itself has no client router; these paths are the whole
     "routing".
     /invite is NOT here any more: the waitlist is a static document at
     web/public/invite/index.html, served by Pages ahead of this bundle, so the
     page keeps the short apex address and ships no app shell at all. */
  const legalDoc = legalDocForPath(window.location.pathname);
  createRoot(container).render(
    <StrictMode>
      <ErrorBoundary>
        {legalDoc ? <Legal doc={legalDoc} /> : <App />}
      </ErrorBoundary>
    </StrictMode>,
  );
} else {
  console.error('[Pear] #root missing — React could not mount');
}

// Offline shell — real deployed origin only (https, never localhost/127.0.0.1).
// Registration failures are swallowed so they can never break a walk or e2e run.
const { protocol, hostname } = window.location;
const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
if ('serviceWorker' in navigator && protocol === 'https:' && !isLocal) {
  try {
    navigator.serviceWorker
      .register('/service-worker.js')
      .catch((error) => console.warn('[Pear] SW registration failed (continuing):', error));
  } catch (error) {
    console.warn('[Pear] SW registration threw (continuing):', error);
  }
}

/* Cache buster that does not wait for a refresh (founder 2026-10-17): re-read
   index.html on a timer and on return-to-tab, and OFFER the reload when the
   entry bundle's hash has moved on — never take it (pre-deploy review 4).
   Runs on the dev origin too; there the entry src never changes, so it costs
   one no-store fetch a minute and does nothing. */
startBuildWatch();
